import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LockIcon, MailIcon, AlertCircleIcon, Eye, EyeOff } from 'lucide-react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code.
const ENABLE_LOGIN_CAPTCHA = true;
// Number of failed login attempts before Turnstile is required for further
// attempts in the same page session. 3 catches automation early while
// tolerating honest typos. See LOGIN_CAPTCHA_THRESHOLD diagnosis notes.
const LOGIN_CAPTCHA_THRESHOLD = 3;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
// Cloudflare's Turnstile JS API URL. Loaded once per page via <script>.
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
export const TeacherLogin = () => {
  const [email, setEmail] = useState('');
  const [passport, setPassport] = useState('');
  const [showPassport, setShowPassport] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // ─── Failed-attempt tracking + Turnstile state ───────────────────────
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const {
    login,
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const captchaRequired = ENABLE_LOGIN_CAPTCHA && failedAttempts >= LOGIN_CAPTCHA_THRESHOLD;
  useEffect(() => {
    // Check if user just verified email and should auto-redirect
    const emailJustVerified = localStorage.getItem('emailJustVerified');
    const verificationTimestamp = localStorage.getItem('verificationTimestamp');
    if (emailJustVerified === 'true' && verificationTimestamp) {
      const timestamp = parseInt(verificationTimestamp, 10);
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      // If verification happened within last 5 minutes
      if (now - timestamp < fiveMinutes) {
        console.log('[TeacherLogin] Email just verified, checking auth state');
        // Check if user is already logged in
        if (currentUser && currentUser.emailVerified) {
          console.log('[TeacherLogin] User verified and logged in, redirecting to dashboard');
          // Clear flags
          localStorage.removeItem('emailJustVerified');
          localStorage.removeItem('verificationTimestamp');
          // Redirect to dashboard
          navigate('/teacher-dashboard');
        }
      } else {
        // Clear expired flags
        localStorage.removeItem('emailJustVerified');
        localStorage.removeItem('verificationTimestamp');
      }
    }
    // Check for verified query parameter
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === 'true') {
      setMessage('Email verified successfully! Please log in to continue.');
    }
  }, [currentUser, navigate]);
  // ─── Load Turnstile script once (idempotent across components) ──────
  useEffect(() => {
    if (!ENABLE_LOGIN_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return;
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  // ─── Render the Turnstile widget once the threshold is reached ──────
  useEffect(() => {
    if (!captchaRequired) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return false;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true;
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [captchaRequired]);
  const resetTurnstileWidget = () => {
    if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
    setTurnstileToken(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !passport) {
      setError('Please enter both email and passport');
      return;
    }
    // ─── Turnstile gate (only active once threshold is reached) ──────
    if (captchaRequired) {
      if (!turnstileToken) {
        setError('Please complete the verification challenge before signing in.');
        return;
      }
      setIsLoading(true);
      const honeypotEl = document.getElementById('mc-tl-website-hp') as HTMLInputElement | null;
      const honeypot = honeypotEl?.value ?? '';
      const verifyFn = httpsCallable<{
        token: string;
        honeypot: string;
      }, {
        verified: boolean;
        errors?: string[];
      }>(functions, 'verifyTurnstile');
      let verifyResult;
      try {
        verifyResult = await verifyFn({
          token: turnstileToken,
          honeypot
        });
      } catch (verifyErr) {
        console.error('[Turnstile] verification call failed:', verifyErr);
        setError('Verification could not be completed. Please refresh the challenge and try again.');
        resetTurnstileWidget();
        setIsLoading(false);
        return;
      }
      if (!verifyResult.data?.verified) {
        console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
        setError('Verification failed. Please complete the challenge again and resubmit.');
        resetTurnstileWidget();
        setIsLoading(false);
        return;
      }
    } else {
      setIsLoading(true);
    }
    try {
      console.log('=== TEACHER LOGIN ATTEMPT ===');
      console.log('Email:', email);
      console.log('Calling login with type: teacher');
      await login(email, passport, 'teacher');
      console.log('Login successful, navigating to dashboard');
      // Reset failure counter on success
      setFailedAttempts(0);
      navigate('/teacher-dashboard');
    } catch (err: any) {
      console.error('=== TEACHER LOGIN ERROR ===');
      console.error('Login error:', err);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      // Determine if this is a credential-class failure (attacker signal)
      // vs a transient/environmental failure (not an attacker signal).
      // Only credential failures should increment the counter.
      const isNetwork = err.code === 'auth/network-request-failed';
      const isFirebaseRateLimit = err.code === 'auth/too-many-requests';
      if (isFirebaseRateLimit) {
        setError('Too many failed login attempts. Please try again later.');
      } else if (isNetwork) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
        // Increment only on actual credential failures
        setFailedAttempts(n => n + 1);
      }
      // Always refresh the challenge after a failed attempt while the gate
      // is active, so a fresh token is required for the next retry.
      if (captchaRequired) {
        resetTurnstileWidget();
      }
    } finally {
      setIsLoading(false);
    }
  };
  const submitDisabled = isLoading || captchaRequired && !turnstileToken;
  return <div className="min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] flex items-center justify-center px-4 py-12" data-id="element-8598">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl overflow-hidden" data-id="element-8599">
        <div className="px-8 py-10" data-id="element-8600">
          <div className="text-center mb-10" data-id="element-8601">
            <h2 className="text-3xl font-bold text-navy-900 mb-2" data-id="element-8602">
              Teacher Login
            </h2>
            <p className="text-gray-600" data-id="element-8603">Sign in to access your dashboard</p>
          </div>

          {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-start" data-id="element-8604">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-8605" />
              <span data-id="element-8606">{error}</span>
            </div>}

          {message && <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 flex items-start" data-id="element-8607">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-8608" />
              <span data-id="element-8609">{message}</span>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-8610">
            <div data-id="element-8611">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8612">
                Email Address
              </label>
              <div className="relative" data-id="element-8613">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8614">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-8615" />
                </div>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="you@example.com" required disabled={isLoading} data-id="element-8616" />
              </div>
            </div>

            <div data-id="element-8617">
              <div className="flex items-center justify-between mb-1" data-id="element-8618">
                <label htmlFor="passport" className="block text-sm font-medium text-gray-700" data-id="element-8619">
                  Passport
                </label>
                <Link to="/forgot-password?role=teacher" className="text-sm text-blue-600 hover:text-blue-700" data-id="element-8620">
                  Forgot password?
                </Link>
              </div>
              <div className="relative" data-id="element-8621">
                <input type={showPassport ? 'text' : 'password'} id="passport" value={passport} onChange={e => setPassport(e.target.value)} className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Enter your passport" required disabled={isLoading} data-id="element-8622" />
                <button type="button" onClick={() => setShowPassport(!showPassport)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none" aria-label={showPassport ? 'Hide passport' : 'Show passport'} data-id="element-8623">
                  {showPassport ? <EyeOff size={20} data-id="element-8624" /> : <Eye size={20} data-id="element-8625" />}
                </button>
              </div>
            </div>

            <div className="flex items-center" data-id="element-8626">
              <input id="remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" data-id="element-8627" />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-8628">
                Remember me
              </label>
            </div>

            {/* ─── Honeypot (visually hidden, real users never fill) ─── */}
            {captchaRequired && <div aria-hidden="true" style={{
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }} data-id="element-8629">
                <label htmlFor="mc-tl-website-hp" data-id="element-8630">Website (leave blank)</label>
                <input id="mc-tl-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-8631" />
              </div>}

            {/* ─── Cloudflare Turnstile widget (only after threshold) ─── */}
            {captchaRequired && <div data-id="element-8632">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8633">
                  Human Verification
                </label>
                <div className="flex justify-center" data-id="element-8634">
                  <div ref={turnstileContainerRef} data-id="element-8635" />
                </div>
                <p className="mt-2 text-xs text-gray-500 text-center" data-id="element-8636">
                  Additional verification is required after multiple failed
                  sign-in attempts.
                </p>
              </div>}

            <button type="submit" disabled={submitDisabled} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8637">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-8638">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-8639"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-8640"></path>
                  </svg>
                  Signing In...
                </> : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 text-center" data-id="element-8641">
            <p className="text-gray-600" data-id="element-8642">
              Don't have an account?{' '}
              <Link to="/teacher-signup" className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-8643">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>;
};